import * as React from "react";
import EdButton from "../../generic/edButton";
import InputField from "../../generic/inputField";
import NewPasswordField from "../generic/newPasswordField";
import "../../../assets/scss/components/login/forms/changePasswordForm.scss";
import "../../../assets/scss/components/login/generic/loginInput.scss";
import Loader from "../../generic/loader";
import { equalTo } from "../../utils/regex";
import { getPreLoginData, getAccData } from "../../service/login/loginService";
import { EResetSubmitState } from "../loginConstants";
import LoginErrMsg from "../generic/loginErrMsg";
import { IChangePassword } from "../../types/components/login/loginTypes";

export interface IChangePasswordFormProps {
	submitState?: EResetSubmitState; // there are three submit states. Normal if undefined.
	errorMsg?: string; // used to show error message if any
	loginIdState?: string; // used to show error; initially it should be undefined. if "success" then show green tick, otherwise show as error
	// onSubmit: function to be called on submit, if resetLoginId is true, then only call the api to reset login id
	onSubmit: (values: IChPwFields, resetLoginId?: boolean) => void;
	onRemoveError: () => void; // removes the errorMsg
}

export interface IChPwFields extends IChangePassword {
	loginId: string;
}

const ChangePasswordForm: React.FC<IChangePasswordFormProps> = (props) => {
	// used to show/hide the old and confirm passwords. Show/hide of new password is handled internally
	const [showPassword, setShowPassword] = React.useState({ old: false, confirm: false });

	// if this is true then only user can edit loginId
	// if this is true the change userid api will run otherwise not
	const [editLoginId, setEditLoginId] = React.useState(false);

	// used to store the entered values in form
	const [values, setValues] = React.useState<IChPwFields>({ loginId: getPreLoginData("userID"), olPwd: "", nePwd: "", coPwd: "" });

	// used to store the field wise errors
	const [errors, setErrors] = React.useState<{ [key in keyof IChPwFields]?: string }>({
		loginId: "",
		olPwd: undefined,
		nePwd: undefined,
		coPwd: undefined
	});

	function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault();

		// submit only if new and old passwords are same
		if (compareOldNewPw()) {
			props.onSubmit(values, props.loginIdState !== "success" && editLoginId);
		}
	}

	function compareOldNewPw() {
		if (values.nePwd !== values.coPwd) {
			setErrors({ ...errors, coPwd: "Confirm Password should be same as new Password" });
			return false;
		} else {
			setErrors({ ...errors, coPwd: "" });
			return true;
		}
	}

	const loginIdHelpText = "You can change your Login ID. Once changed, it cannot be changed again";

	// submit button text on different states
	const verLoginIdBtn = (
		<>
			Verifying Login ID <Loader size={20} />
		</>
	);
	const verPasswordBtn = (
		<>
			Verifying Password <Loader size={20} />
		</>
	);

	// if userid is wrong then set it as an error
	React.useEffect(() => {
		if (props.loginIdState && props.loginIdState !== "success") {
			setErrors({ ...errors, loginId: props.loginIdState });
		}
	}, [props.loginIdState, props.submitState]);

	React.useEffect(() => {
		props.onRemoveError();
	}, [values])

	function getLoginField() {
		if (props.loginIdState === "success") {
			return (
				<div className="input_field_wrapper login_inp mg-b40">
					<span className="login_id">{values.loginId}</span>
					<i className="edel-icon-tick" />
				</div>
			);
		}

		if (editLoginId) {
			// If editLoginId is true then show input field
			return (
				<InputField
					onChange={(loginId) => {
						setValues({ ...values, loginId });
						setErrors({ ...errors, loginId: "" });
					}}
					defaultValue={values.loginId}
					onBlur={(error) => setErrors({ ...errors, loginId: error.required ? "Field is required" : "" })}
					className="login_inp mg-b40"
					placeholder="Eg:5678987"
					maxLength={24}
					required
					helpText={errors.loginId ? undefined : loginIdHelpText}
					helpTextColor="blue"
					errorText={errors.loginId}
				/>
			);
		}

		// otherwise show div styled as input field (user can't edit)
		return (
			<div className="input_field_wrapper login_inp mg-b40">
				<div className="input_field_cont">
					<span className="input_field">{values.loginId}</span>
					<div className="input_suffix">
						<i className="edel-icon-edit icon" onClick={() => setEditLoginId(true)} />
					</div>
				</div>
				<p className="help_text blue">{loginIdHelpText}</p>
			</div>
		);
	}

	return (
		<form onSubmit={handleSubmit} className={props.submitState ? "submit" : ""}>
			{/* If rstusr is true, then show as input field */}
			{getAccData("accs").rstusr && <label className="inp_lbl mg-b5 ib">Login ID</label>}
			{getAccData("accs").rstusr && getLoginField()}

			<label className="inp_lbl mg-b5 ib">Old Password</label>
			<InputField
				type={showPassword.old ? "text" : "password"}
				onChange={(olPwd) => {
					setValues({ ...values, olPwd });
					setErrors({ ...errors, olPwd: "" });
				}}
				onBlur={(error) => setErrors({ ...errors, olPwd: error.required ? "Field is required" : "" })}
				className="login_inp mg-b40"
				placeholder="Eg:5678987"
				required
				suffix={
					<i
						className={`edel-icon-eye${showPassword.old ? "-cross" : ""} icon`}
						onClick={() => setShowPassword({ ...showPassword, old: !showPassword.old })}
					/>
				}
				errorText={errors.olPwd}
			/>

			<label className="inp_lbl mg-b5 ib">New Password</label>
			<NewPasswordField
				onChange={(nePwd, isValid) => {
					setValues({ ...values, nePwd });
					setErrors({ ...errors, nePwd: isValid });
				}}
				onBlur={(error) => setErrors({ ...errors, nePwd: error })}
			/>

			<label className="inp_lbl mg-b5 ib">Confirm New Password</label>
			<InputField
				type={showPassword.confirm ? "text" : "password"}
				onChange={(coPwd) => {
					setValues({ ...values, coPwd });
					setErrors({ ...errors, coPwd: "" });
				}}
				onBlur={(error) => setErrors({ ...errors, coPwd: error.pattern ? "" : "Entered password is not matching. Please confirm again" })}
				pattern={equalTo(values.nePwd)}
				className="login_inp mg-b40"
				placeholder="Eg:5678987"
				suffix={
					<i
						className={`edel-icon-eye${showPassword.confirm ? "-cross" : ""} icon`}
						onClick={() => setShowPassword({ ...showPassword, confirm: !showPassword.confirm })}
					/>
				}
				errorText={errors.coPwd}
			/>

			{/* Show error if any */}
			{props.errorMsg && <LoginErrMsg className="mg-b40">{props.errorMsg}</LoginErrMsg>}

			<EdButton
				value={!props.submitState ? "Confirm" : props.submitState === EResetSubmitState.ver_login_id ? verLoginIdBtn : verPasswordBtn}
				btnTyp="primary"
				className="submit_btn"
				// Disable if there is even one error or form is submitted
				disabled={Object.values(errors).some((error) => error !== "") || !!props.submitState || values.nePwd !== values.coPwd}
			/>
		</form>
	);
};

export default ChangePasswordForm;
